import StyleSelect from '@chegg-wt/core/components/StyleSelect'
import { AppState } from '@chegg-wt/core/state'
import {
  COPY_SUCCESS_INTERSTITIAL_MODAL,
  COPY_SUCCESS_MODAL,
  ONE_AUTH_MODAL,
} from '@chegg-wt/core/state/modals/constants'
import { copyAll } from '@chegg-wt/core/utils/copy'
import React from 'react'
import { connect, useSelector } from 'react-redux'
import { OpenModalPayload } from '@chegg-wt/core/state/modals/types'
import {
  origins,
  UserClickPayload,
} from '@chegg-wt/core/state/instrumentation/rio/clickstreamInteractions'
import {
  AuthModalData,
  AuthModalOrigin,
} from '@chegg-wt/core/components/Modals/AuthModal/types'
import {
  INTERSTITIAL_AD_CTFM_EB_TEST_CELL,
  VIEWPAGE_PLAGIARISM_EXPORT,
} from '@chegg-wt/core/state/optimizely/constants'
import { useSafeOptimizelyFeature } from '@chegg/optimizely-sdk'
import {
  CHANGE_STYLE_BTN,
  COPY_ALL_BTN,
  EXPORT_ALL_BTN,
} from '@chegg-wt/core/constants/biblography'
import { Citation } from '@chegg-wt/core/types/redux-citation-api'
import { userClick } from '@chegg-wt/core/state/instrumentation/actions'
import { openModal } from '@chegg-wt/core/state/modals/actions'
import { sortedCitationsSelector } from '@chegg-wt/core/state/bibliography/selectors'
import { shouldShowValidatorWarningSelector } from '@chegg-wt/core/state/citation/selectors'
import { currentStyleNameSelector } from '@chegg-wt/core/state/currentProject/selectors'
import { loggedInSelector, userRoleSelector } from '@chegg-wt/core/state/user/selectors'
import { styleIdToAlias, transformDeprecatedStyleId } from '@chegg-wt/core/utils/styles'
import { adConfigVersionSelector } from '@chegg-wt/core/state/featureFlags/selectors'
import { UserRole } from '@chegg-wt/core/constants/user'
import { Config } from '@chegg-wt/core/config'
import CreateCitationButton from '../ViewPageTop/CreateCitationButton'
import * as Styled from './styled'

interface ExportButtonProps {
  openModal: (payload: OpenModalPayload) => void
  onExport: (citations: Citation[]) => void
  citations: Citation[]
  userClick: (payload: UserClickPayload) => void
}

export interface Props extends ExportButtonProps {
  shouldShowWarning?: boolean
  currentStyleName: string
  adConfigVersion: string
  userRole: UserRole
  onStyleChangeSelect: (id: string, name: string) => void
  onStyleChangeClick: () => void
  userClick: (payload: UserClickPayload) => void
}

export const ExportButton: React.FC<ExportButtonProps> = ({
  openModal,
  onExport,
  citations,
  userClick,
}) => {
  const isLoggedIn = useSelector(loggedInSelector)
  const { variables } = useSafeOptimizelyFeature(VIEWPAGE_PLAGIARISM_EXPORT)
  const { withExportGate } = variables
  const handleclick = () => {
    userClick({ origin: origins.exportAllCitationsClick })
    if (withExportGate && !isLoggedIn) {
      const data: AuthModalData = {
        defaultFlow: 'signup',
        origin: AuthModalOrigin.VIEW_PAGE_EXPORT,
      }
      openModal({ name: ONE_AUTH_MODAL, data })
    } else {
      onExport(citations)
    }
  }

  return (
    <Styled.ExportButton size="medium" data-test={EXPORT_ALL_BTN} onClick={handleclick}>
      <>
        <Styled.Export size="medium" />
        <span>Export all</span>
      </>
    </Styled.ExportButton>
  )
}

export const ButtonContainer: React.FC<Props> = ({
  citations,
  shouldShowWarning = false,
  openModal,
  currentStyleName,
  adConfigVersion,
  userRole,
  onStyleChangeClick,
  onStyleChangeSelect,
  onExport,
  userClick,
}) => {
  const hasCitations = citations.length > 0

  return (
    <Styled.CitationCTAContainer>
      <Styled.CitationButtonCTAContainer>
        <CreateCitationButton />
        <StyleSelect
          trigger={({ isOpen, openDropdown }) => (
            <Styled.CitationStyleButton
              onClick={() => {
                openDropdown()
                onStyleChangeClick()
              }}
              data-test={CHANGE_STYLE_BTN}
              aria-expanded={isOpen}
            >
              <Styled.ButtonText>{currentStyleName}</Styled.ButtonText>
              <Styled.Chevron pointing={isOpen ? 'up' : 'down'} />
            </Styled.CitationStyleButton>
          )}
          onSelectCallback={onStyleChangeSelect}
        />
      </Styled.CitationButtonCTAContainer>

      {/* NOTE (Re: WTAC-256) If this redesign test wins, QA tests may break due to missing Save button  */}

      {hasCitations && (
        <Styled.CitationUtilityCTAContainer>
          <Styled.CopyButton
            size="medium"
            data-test={COPY_ALL_BTN}
            onClick={() => {
              copyAll(citations)
              userClick({ origin: origins.copyAllCitationsClick })
              openModal({
                name:
                  userRole !== 'mybibpro' &&
                  (Config.showInterstitialAd ||
                    adConfigVersion === INTERSTITIAL_AD_CTFM_EB_TEST_CELL)
                    ? COPY_SUCCESS_INTERSTITIAL_MODAL
                    : COPY_SUCCESS_MODAL,
                data: {
                  text: 'All citations',
                  shouldShowWarning,
                },
              })
            }}
          >
            <>
              <Styled.ImageWrapper>
                <Styled.CopyImage />
              </Styled.ImageWrapper>
              <span>Copy all </span>
            </>
          </Styled.CopyButton>
          <ExportButton
            openModal={openModal}
            onExport={onExport}
            citations={citations}
            userClick={userClick}
          />
        </Styled.CitationUtilityCTAContainer>
      )}
    </Styled.CitationCTAContainer>
  )
}

const mapStateToProps = (state: AppState) => ({
  citations: sortedCitationsSelector(state),
  shouldShowWarning: shouldShowValidatorWarningSelector(state),
  currentStyleName: currentStyleNameSelector(state),
  adConfigVersion: adConfigVersionSelector(state),
  userRole: userRoleSelector(state),
})

const mapDispatchToProps = {
  openModal,
  userClick,
  onStyleChangeClick: () => userClick({ origin: origins.styleChangeClick }),
  onStyleChangeSelect: (id: string, name: string) =>
    userClick({
      origin: origins.styleChangeSelect,
      value: name,
      style: styleIdToAlias(transformDeprecatedStyleId(id)),
    }),
}

export default connect(mapStateToProps, mapDispatchToProps)(ButtonContainer)
